import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionsButton from 'src/components/actions-button/ActionsButton';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import TabHeader from 'src/components/tab-header';
import { useGetIndicatorsByParentId } from 'src/hooks/queries';
import { useSmartCopyAmplitude } from 'src/hooks/smart-copy';
import type { IndicatorFormDataFields } from 'src/pages/indicators/forms/indicatorSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { getSmartCopyTemplateActionState } from 'src/utils/smart-copy';

import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useDeleteIndicators } from '@/hooks/mutations/indicator/useDeleteIndicators';
import { useInsertIndicator } from '@/hooks/mutations/indicator/useInsertIndicator';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';

import CreateIndicatorModal from '../modals/CreateIndicatorModal';
import { useGetCollectionTableProps } from './config';
import { INDICATOR_SMART_COPY } from './indicatorSmartCopy';
import type { IndicatorTableFields } from './types';

interface Props {
  parent: ObjectWithContributors;
  parentType: Parent_Type_Enum;
}

const Tab: FC<Props> = ({ parent, parentType }) => {
  useI18NSummaryHelpContent('indicators.tabHelp');
  const { updateFiles } = useFileUpdate();
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'indicators' });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const {
    hasPermission: canDeleteIndicators,
    loading: canDeleteIndicatorsLoading,
  } = useHasPermissionQuery('delete:indicator', parent);
  const {
    hasPermission: canInsertIndicators,
    loading: canInsertIndicatorsLoading,
  } = useHasPermissionQuery('insert:indicator', parent);
  const isSmartCopyEnabled = useIsFeatureFlagEnabled(
    INDICATOR_SMART_COPY.flagKey
  );
  // Standing props on every Smart Copy event. Memoised as it feeds the hook's
  // `logEvent` useCallback; `parent_type` is the module key (risk/control/obligation).
  const smartCopyBaseProps = useMemo(
    () => ({ parent_type: parentType, parentId: parent.Id }),
    [parentType, parent.Id]
  );
  const { events, logEvent } = useSmartCopyAmplitude({
    entityLabel: INDICATOR_SMART_COPY.eventLabel,
    baseProps: smartCopyBaseProps,
  });
  const [selected, setSelected] = useState<IndicatorTableFields[]>([]);
  // Source Indicator to pre-populate the create modal from; undefined for a
  // blank create.
  const [sourceIndicatorId, setSourceIndicatorId] = useState<
    string | undefined
  >();

  const { data, loading, refetch } = useGetIndicatorsByParentId({
    queryArgs: { parentId: parent.Id },
  });

  const { deleteIndicators, loading: deleteLoading } = useDeleteIndicators();
  const { insertIndicator } = useInsertIndicator();
  const handleCreateOpen = () => {
    // Blank create — ensure no source is carried over from a prior template open.
    setSourceIndicatorId(undefined);
    setIsCreateOpen(true);
  };
  const handleCreateClose = () => {
    setIsCreateOpen(false);
    setSourceIndicatorId(undefined);
  };
  const handleAddFromTemplate = () => {
    // Guard the no-op locally rather than relying on Cloudscape to suppress
    // clicks on the disabled (0/2+ selected) item.
    if (selected.length !== 1) {
      return;
    }
    // Reachable only via the flag-gated dropdown, so no explicit flag check.
    logEvent(events.TEMPLATE_SELECTED, {
      sourceIndicatorId: selected[0].Id,
      indicator_type: selected[0].Type,
    });
    // Pre-populate the create modal from the single selected Indicator.
    setSourceIndicatorId(selected[0].Id);
    setIsCreateOpen(true);
  };

  const onDelete = useDeleteResultNotification({
    asyncAction: async () => {
      await deleteIndicators(selected.map((s) => s.Id));
      setSelected([]);
      setIsDeleteModalVisible(false);
      refetch();

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
    entityName: st('entity_name'),
  });

  const save = async (data: IndicatorFormDataFields) => {
    const { files, ...rest } = data;
    const result = await insertIndicator({
      ParentId: parent.Id,
      CustomAttributeData: data.CustomAttributeData || undefined,
      DepartmentTypeIds: rest.departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: rest.tags?.map((t) => t.TagTypeId) || [],
      Description: rest.Description,
      LowerAppetiteNum:
        rest.Type === 'number' ? rest.LowerAppetiteNum : undefined,
      LowerToleranceNum:
        rest.Type === 'number' ? rest.LowerToleranceNum : undefined,
      TargetValueTxt: rest.Type === 'text' ? rest.TargetValueTxt : undefined,

      Title: rest.Title,
      Type: rest.Type,
      Unit: rest.Unit,
      UpperAppetiteNum:
        rest.Type === 'number' ? rest.UpperAppetiteNum : undefined,
      UpperToleranceNum:
        rest.Type === 'number' ? rest.UpperToleranceNum : undefined,
      schedule: rest.schedule,
      ...ownerContributorAndViewerIds(data),
    });
    const indicatorId = result.insertChildIndicator?.Id;
    if (!indicatorId) {
      throw new Error('Missing indicator id');
    }
    await updateFiles({
      parentType: Parent_Type_Enum.Indicator,
      parentId: indicatorId,
      originalFiles: [],
      selectedFiles: files,
    });
    refetch();

    // Fire only on the flag-on path and only after a successful insert
    // (unreachable if the insert threw above). A source Indicator means a
    // template save, otherwise a blank save.
    if (isSmartCopyEnabled) {
      if (sourceIndicatorId) {
        logEvent(events.SMART_COPY_SAVED, { sourceIndicatorId });
      } else {
        logEvent(events.BASELINE_SAVED, {});
      }
    }

    // After a template save, clear the selection so the "from template" option
    // returns to its disabled state.
    if (sourceIndicatorId) {
      setSelected([]);
    }

    return { savedId: indicatorId };
  };

  const tableProps = useGetCollectionTableProps(
    data?.indicator,
    handleCreateOpen,
    parent
  );

  const templateActionState = getSmartCopyTemplateActionState(t, {
    entityNoun: INDICATOR_SMART_COPY.entityNoun,
    selectedCount: selected.length,
  });

  return (
    <>
      <Table
        {...tableProps}
        selectionType={
          (canDeleteIndicators && !canDeleteIndicatorsLoading) ||
          (isSmartCopyEnabled &&
            canInsertIndicators &&
            !canInsertIndicatorsLoading)
            ? 'multi'
            : undefined
        }
        selectedItems={selected}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => {
          setSelected(detail.selectedItems);
        }}
        header={
          <SpaceBetween size={'m'}>
            <TabHeader
              actions={
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  <Permission
                    permission={'delete:indicator'}
                    parentObject={parent}
                  >
                    <Button
                      formAction={'none'}
                      variant={'normal'}
                      disabled={!selected.length}
                      onClick={() => setIsDeleteModalVisible(true)}
                    >
                      {st('delete_button')}
                    </Button>
                  </Permission>
                  <Permission
                    permission={'insert:indicator'}
                    parentObject={parent}
                  >
                    {isSmartCopyEnabled ? (
                      <ActionsButton
                        onOpen={() => logEvent(events.DROPDOWN_OPENED, {})}
                        buttonText={st('actions_button')}
                        testId={'indicatorActions'}
                        variant={'primary'}
                        items={[
                          {
                            id: 'addIndicator',
                            text: st('add_indicator'),
                            onItemClick: handleCreateOpen,
                          },
                          {
                            id: 'addIndicatorFromTemplate',
                            text: st('add_indicator_from_template'),
                            ...templateActionState,
                            onItemClick: handleAddFromTemplate,
                          },
                        ]}
                      />
                    ) : (
                      <Button
                        variant={'primary'}
                        formAction={'none'}
                        onClick={handleCreateOpen}
                      >
                        {st('create_new_button')}
                      </Button>
                    )}
                  </Permission>
                </SpaceBetween>
              }
            >
              {st('tab_title')}
            </TabHeader>
          </SpaceBetween>
        }
        variant={'embedded'}
        loading={loading}
        loadingText={st('loading_message') ?? ''}
        sortingDisabled={false}
      />

      {isCreateOpen && (
        <CreateIndicatorModal
          onSave={save}
          onDismiss={handleCreateClose}
          sourceIndicatorId={sourceIndicatorId}
        />
      )}

      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};

export default Tab;
